import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useSetupChecklist from '../hooks/useSetupChecklist';
import { useUI } from '../contexts';
import { recordEvent } from '../functions';
import '../styles/SetupChecklistWidget.css';

// Routes where the "finish setup" nudge should never appear: onboarding/auth
// flows (the user is mid-setup) and the Integrations page itself (they're
// already where the checklist would send them).
const HIDDEN_PREFIXES = [
    '/onboarding',
    '/sign-up',
    '/login',
    '/welcome',
    '/get-started',
    '/company-setup',
    '/integrations',
];

function isHiddenRoute(pathname) {
    return HIDDEN_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

function SetupChecklistWidget() {
    const location = useLocation();
    const navigate = useNavigate();
    const { showDarkBackground } = useUI();
    const { items, completedCount, total, allDone, ready } = useSetupChecklist();

    const [open, setOpen] = useState(false);
    const [celebrationDone, setCelebrationDone] = useState(false);
    // Tracks whether the user has ever seen the widget in an incomplete state,
    // so we only fire the "all set" celebration for someone who actually
    // finished here — not for an account that arrives already complete.
    const wasIncompleteRef = useRef(false);

    useEffect(() => {
        if (ready && !allDone) wasIncompleteRef.current = true;
    }, [ready, allDone]);

    const showCelebration = allDone && wasIncompleteRef.current && !celebrationDone;

    useEffect(() => {
        if (!showCelebration) return undefined;
        const t = setTimeout(() => setCelebrationDone(true), 4000);
        return () => clearTimeout(t);
    }, [showCelebration]);

    if (!ready || isHiddenRoute(location.pathname)) return null;
    // Hide whenever an overlay/modal is open (showDarkBackground is the
    // app-wide signal set by every modal-bearing page) so the floating
    // pill doesn't sit on top of dialogs.
    if (showDarkBackground) return null;
    if (allDone && !showCelebration) return null;

    if (showCelebration) {
        return (
            <div className="scw-root">
                <div className="scw-card scw-card--celebrate">
                    <div className="scw-celebrate-check">
                        <svg width="20" height="20" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                            <path d="M3.5 8.5l3 3 6-7" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>
                    <span className="body-semibold colour-primary">You're all set!</span>
                    <span className="label-regular colour-secondary">Your account is fully connected.</span>
                </div>
            </div>
        );
    }

    const pct = Math.round((completedCount / total) * 100);

    const handleToggle = () => {
        const next = !open;
        setOpen(next);
        recordEvent('setup_checklist_toggled', { open: next, completedCount });
    };

    const handleItemClick = (item) => {
        recordEvent('setup_checklist_item_clicked', { item: item.id });
        navigate(item.href);
    };

    return (
        <div className="scw-root">
            {open && (
                <div className="scw-card" role="dialog" aria-label="Finish setting up your account">
                    <div className="scw-card-head">
                        <span className="body-semibold colour-primary">Finish setting up</span>
                        <span className="label-regular colour-secondary">{completedCount} of {total} done</span>
                    </div>

                    <div className="scw-progress-bar">
                        <div className="scw-progress-fill" style={{ '--scw-pct': `${pct}%` }} />
                    </div>

                    <div className="scw-list">
                        {items.map((item) => (
                            <div
                                key={item.id}
                                className={`scw-item${item.done ? ' scw-item--done' : ''}`}
                            >
                                <div className={`scw-item-circle${item.done ? ' scw-item-circle--done' : ''}`}>
                                    {item.done ? (
                                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                                            <path d="M3.5 8.5l3 3 6-7" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    ) : null}
                                </div>
                                <div className="scw-item-text">
                                    <span className={`body-semibold${item.done ? ' colour-jade-03' : ' colour-primary'}`}>{item.title}</span>
                                    {!item.done && (
                                        <>
                                            <span className="label-regular colour-secondary">{item.benefit}</span>
                                            <button
                                                type="button"
                                                className="scw-item-cta label-bold cursor-pointer"
                                                onClick={() => handleItemClick(item)}
                                            >
                                                {item.cta} →
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <button
                type="button"
                className="scw-pill cursor-pointer"
                onClick={handleToggle}
                aria-expanded={open}
            >
                <span className="scw-pill-ring" style={{ '--scw-pct': `${pct}%` }}>
                    <span className="label-bold colour-primary">{completedCount}/{total}</span>
                </span>
                <span className="scw-pill-label body-semibold colour-primary">Finish setup</span>
                <svg
                    className={`scw-pill-chevron${open ? ' scw-pill-chevron--open' : ''}`}
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    aria-hidden="true"
                >
                    <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>
        </div>
    );
}

export default SetupChecklistWidget;
